import { useEffect, useState } from 'react'
import { Trans } from 'next-i18next'
import { Box, Text, TextLinkButton, useToast } from '@machobear/design-system'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { useOpenInWindow } from 'use-open-window'

import { verifyUsername } from '@/models/auth/ports'
import { useDispatch } from '@/lib/redux'
import Yup from '@/lib/yup'
import getErrorMessage from '@/utils/get-error'
import handleSession from '@/utils/handle-session'
import { SocialButtons } from '@/features/Auth/components'
import AgreeToTerms from '@/components/agree-to-terms'
import { Button } from '@/components/button-new'
import { FieldInput, Group, PasswordInput, Row } from '@/components/form'

import { useDSLogin } from '../Context/ds-login-provider'
import { useLogin } from './context'
import * as styles from './styles.css'
import { DSLoginStepProps, Provider } from './types'

type Form = {
  username: string
  password: string
}

export type DSLoginSocialProps = Pick<
  DSLoginStepProps,
  'setCurrentStep' | 'title' | 't' | 'onClose' | 'data' | 'afterLogin2'
>

const LoginSocialForDS: React.FC<DSLoginSocialProps> = ({
  setCurrentStep,
  t,
  onClose,
  afterLogin2,
  data = {}
}) => {
  const dispatch = useDispatch()
  const {
    setSocial,
    setUsername,
    usernameClicked,
    setUsernameClicked,
    setExistingSocialState,
    eventId
  } = useLogin()
  const { afterLogin } = useDSLogin()
  const showToast = useToast()
  const registerRoute = 'register'

  const [selectedPlatform, setSelectedPlatform] = useState<
    Provider | undefined
  >()

  const options = {
    url: '',
    centered: true,
    specs: {
      width: 800,
      height: 600
    }
  }
  const [handleWindowOpen, newWindowHandle] = useOpenInWindow(options)

  useEffect(() => {
    const messageListener = async (event: MessageEvent) => {
      // Handle log in by social platform
      if (event.data.source_message === 'mb_social_login') {
        setSocial({ platform: selectedPlatform, data: event.data })
        if (!newWindowHandle) return
        newWindowHandle.close()
        switch (event.data.message_key) {
          case 'msg.login_with_social_login_unauthorized':
          case 'msg.login_with_social_login_unauthorized_by_provider':
            setExistingSocialState(true)
            setCurrentStep('existingSocialUser')
            break
          case 'err.unauthorized_token':
          case 'err.email_was_not_sent_by_provider':
            // Note this error seems to always occur on dev
            showToast({
              message: t(
                'b.auth.view.login_password.failed.title',
                'Login Failed'
              ),
              description: event?.data?.message,
              tone: 'critical'
            })
            return
          default:
            if (event.data.new === 0) {
              await handleSession(dispatch.session.loadSession())
              afterLogin()
              afterLogin2()
              return onClose()
            }
            // If no account connected to social login, show register instead
            setCurrentStep(registerRoute)
        }
      }
    }
    window.addEventListener('message', messageListener)

    return () => {
      window.removeEventListener('message', messageListener)
    }
  }, [
    setExistingSocialState,
    newWindowHandle,
    setCurrentStep,
    setSocial,
    selectedPlatform,
    dispatch,
    t,
    registerRoute,
    onClose,
    afterLogin,
    afterLogin2,
    showToast
  ])

  const initialValues = { username: data.username ?? '', password: '' }
  const validationSchema = Yup.object().shape({
    username: Yup.string().phoneEmail?.(),
    password: Yup.string().required()
  })
  const onSubmit = async (
    values: Form,
    { setSubmitting }: FormikHelpers<Form>
  ) => {
    setUsername(values.username)

    const { body } = await verifyUsername({ username: values.username })

    if (
      body.login_with_email_enabled === false &&
      body.login_with_social_account_enabled === true
    ) {
      setSubmitting(false)
      showToast({
        message: t('b.auth.view.login_password.failed.title', 'Login Failed'),
        description: (
          <Trans
            i18nKey="b.auth.view.use_provider.failed.text"
            values={{ provider: body.connected_social_providers[0] }}
            defaults="Use your {{ provider }} provider to login."
          />
        ),
        tone: 'critical'
      })
    }

    const { error, sessionBody } = await dispatch.auth.login(values)

    if (error) {
      const message = getErrorMessage(error)
      setSubmitting(false)
      if (
        message === 'Invalid login info.' ||
        message ===
          'The string should match pattern: ^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@+.#?*$%^&)(>/<_\\\\.\\-|\\]\\[])(?=.{8,})'
      ) {
        return showToast({
          message: t('b.auth.view.login_password.failed.title', 'Login Failed'),
          description: t(
            'b.auth.view.login_password.failed_invalid.error',
            'The email or password you entered is invalid. Please try again.'
          ),
          tone: 'critical'
        })
      }

      return showToast({
        message: t('b.auth.view.login_password.failed.title', 'Login Failed'),
        description: getErrorMessage(error),
        tone: 'critical'
      })
    }
    afterLogin(sessionBody)
    afterLogin2()

    setSubmitting(false)

    return onClose()
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, errors, touched }: FormikProps<Form>) => (
          <Box component={Form} noValidate>
            <Row>
              <Group col={12}>
                <FieldInput
                  name="username"
                  type="email"
                  autoComplete="section-login-social username email"
                  autoFocus
                  placeholder={t(
                    'b.auth.view.email_or_phone.placeholder',
                    'Email or phone number'
                  )}
                  required
                  hideError={isSubmitting || !usernameClicked}
                  onClick={() => setUsernameClicked(true)}
                />
              </Group>
            </Row>
            <Row>
              <Box
                marginBottom={
                  !isSubmitting && !!(errors['password'] && touched['password'])
                    ? 'none'
                    : 'xxsmall'
                }
                paddingX="xxsmall"
                width="full"
              >
                <PasswordInput
                  name="password"
                  autoComplete="section-login-social current-password"
                  placeholder={t(
                    'b.auth.view.password.placeholder',
                    'Password'
                  )}
                  required
                  hideError={isSubmitting}
                />
              </Box>
            </Row>
            <Box marginBottom="medium">
              <Text size="small">
                <TextLinkButton
                  id="web-password-forgotpassword-button"
                  onClick={() => setCurrentStep('forgotPassword')}
                >
                  {t('b.auth.view.forgot_password.text', 'Forgot password?')}
                </TextLinkButton>
              </Text>
            </Box>
            <Button
              id="web-LoginSocial-submit-button"
              theme="primary"
              type="submit"
              width="100%"
              disabled={isSubmitting || !isValid}
            >
              {t('common.login', 'Log In')}
            </Button>
            <Box display="flex" justifyContent="center" width="full">
              <Box
                className={styles.orTextStyle}
                width="full"
                marginY="gutter"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {t('b.auth.view.or_login_with.text', 'OR LOG IN WITH')}
              </Box>
            </Box>
            <SocialButtons
              onClick={(item, socialItem, event) => {
                handleWindowOpen(event, {
                  url: socialItem.url
                })
                setSelectedPlatform(item)
              }}
              trackingId={eventId}
            />
            <Row>
              <AgreeToTerms implicit t={t} />
            </Row>
          </Box>
        )}
      </Formik>
    </>
  )
}

export default LoginSocialForDS
